/**
 * AdminBillingPage — /coach/admin/billing
 *
 * Invoice management for owners and platform_admins.
 * Features: list invoices, generate monthly invoice, download PDF, email to org owner.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { adminBillingAPI } from '../../services/api';
import CoachLayout from './CoachLayout';

const C = {
  bg: '#1E1E24', surface: '#2a2a32', border: '#3a3a44',
  accent: '#92140C', cream: '#FFF8F0', peach: '#FFCF99',
  muted: '#9ca3af', green: '#22c55e', red: '#ef4444', yellow: '#f59e0b',
};

const STATUS_COLORS = { draft: C.yellow, sent: C.green, paid: '#14b8a6', cancelled: C.red };

function StatusBadge({ status }) {
  return (
    <span style={{
      background: STATUS_COLORS[status] || C.border, color: '#fff',
      borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
    }}>
      {status}
    </span>
  );
}

// ── Generate Invoice Modal ─────────────────────────────────────────────────────

function GenerateModal({ onClose, onGenerated }) {
  const now = new Date();
  const [form, setForm] = useState({
    billing_org_id: '',
    period_year: now.getFullYear(),
    period_month: now.getMonth() + 1,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const generate = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await adminBillingAPI.generateInvoice({
        ...form,
        period_year: parseInt(form.period_year),
        period_month: parseInt(form.period_month),
      });
      setResult(res.data);
      onGenerated();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };

  const inp = { width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 10px', color: C.cream, fontSize: 13, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 28, width: 420, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
          <h3 style={{ color: C.cream, margin: 0 }}>Generate Invoice</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>

        {err && <div style={{ background: '#7f1d1d', color: C.cream, borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12 }}>{err}</div>}

        {result ? (
          <div>
            <div style={{ background: '#14532d', color: C.cream, borderRadius: 8, padding: 14, marginBottom: 14 }}>
              {result.already_exists ? '⚠ Invoice already exists for this period.' : '✓ Invoice generated.'}
              <br />
              <strong>Total: {result.invoice?.total_dollars || '$0.00'}</strong>
              <br />
              <span style={{ fontSize: 11, color: C.muted }}>Invoice ID: {result.invoice?.invoice_id}</span>
            </div>
            <button onClick={onClose} style={{ width: '100%', padding: '9px 0', borderRadius: 6, border: 'none', background: C.accent, color: C.cream, cursor: 'pointer', fontWeight: 600 }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ color: C.muted, fontSize: 12 }}>Billing Org ID *
                <input style={inp} value={form.billing_org_id} placeholder="e.g. ffd"
                  onChange={e => setForm(p => ({ ...p, billing_org_id: e.target.value }))} />
              </label>
              <div style={{ display: 'flex', gap: 10 }}>
                <label style={{ color: C.muted, fontSize: 12, flex: 1 }}>Year *
                  <input style={inp} type="number" min="2024" max="2030" value={form.period_year}
                    onChange={e => setForm(p => ({ ...p, period_year: e.target.value }))} />
                </label>
                <label style={{ color: C.muted, fontSize: 12, flex: 1 }}>Month (1-12) *
                  <input style={inp} type="number" min="1" max="12" value={form.period_month}
                    onChange={e => setForm(p => ({ ...p, period_month: e.target.value }))} />
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: C.border, color: C.cream, cursor: 'pointer' }}>Cancel</button>
              <button onClick={generate} disabled={loading || !form.billing_org_id}
                style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: C.accent, color: C.cream, cursor: 'pointer', fontWeight: 600 }}>
                {loading ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Invoice Detail ─────────────────────────────────────────────────────────────

function InvoiceDetail({ invoice, onClose, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const showMsg = (text, isErr) => { setMsg({ text, isErr }); setTimeout(() => setMsg(null), 4000); };

  const download = async () => {
    try {
      const res = await adminBillingAPI.downloadPdf(invoice.invoice_id);
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoice.period_label}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { showMsg('Download failed', true); }
  };

  const email = async () => {
    setLoading(true);
    try {
      const res = await adminBillingAPI.emailInvoice(invoice.invoice_id);
      showMsg(`Emailed to ${res.data.emailed_to}`);
      onRefresh();
    } catch (e) { showMsg(e?.response?.data?.detail || 'Email failed', true); }
    finally { setLoading(false); }
  };

  const finalize = async () => {
    setLoading(true);
    try {
      await adminBillingAPI.finalizeInvoice(invoice.invoice_id);
      showMsg('Invoice marked as sent.');
      onRefresh();
    } catch { showMsg('Finalize failed', true); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 440, background: C.surface, borderLeft: `1px solid ${C.border}`, zIndex: 1000, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.4)' }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, color: C.cream, fontSize: 15 }}>{invoice.billing_org_id} — {invoice.period_label}</div>
          <StatusBadge status={invoice.status} />
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 20 }}>×</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {msg && (
          <div style={{ background: msg.isErr ? '#7f1d1d' : '#14532d', color: C.cream, borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12 }}>{msg.text}</div>
        )}

        <div style={{ marginBottom: 16 }}>
          <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>Invoice ID</div>
          <div style={{ color: C.cream, fontSize: 12, fontFamily: 'monospace' }}>{invoice.invoice_id}</div>
        </div>

        {/* Line Items */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: C.muted, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Line Items</div>
          {(invoice.line_items || []).length === 0 ? (
            <div style={{ color: C.muted, fontSize: 12 }}>No line items — no active subscriptions found for this period.</div>
          ) : (
            invoice.line_items.map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                <div>
                  <div style={{ color: C.cream, fontSize: 13 }}>{item.description}</div>
                  <div style={{ color: C.muted, fontSize: 11 }}>{item.domain_id} · {item.quantity} × ${(item.unit_price_cents / 100).toFixed(2)}</div>
                </div>
                <div style={{ color: C.peach, fontWeight: 700, fontSize: 14 }}>${(item.total_cents / 100).toFixed(2)}</div>
              </div>
            ))
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: `2px solid ${C.border}` }}>
          <span style={{ color: C.cream, fontWeight: 700, fontSize: 15 }}>Total</span>
          <span style={{ color: C.peach, fontWeight: 700, fontSize: 18 }}>{invoice.total_dollars || '$0.00'}</span>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          <button onClick={download} style={{ padding: '9px 0', borderRadius: 6, border: 'none', background: '#1d4ed8', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            ⬇ Download PDF
          </button>
          <button onClick={email} disabled={loading} style={{ padding: '9px 0', borderRadius: 6, border: 'none', background: '#0d9488', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            {loading ? '…' : '✉ Email to Org Owner'}
          </button>
          {invoice.status === 'draft' && (
            <button onClick={finalize} disabled={loading} style={{ padding: '9px 0', borderRadius: 6, border: 'none', background: '#4b5563', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              ✓ Finalize (mark as sent)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function AdminBillingPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [orgFilter, setOrgFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (orgFilter) params.billing_org_id = orgFilter;
      if (yearFilter) params.period_year = parseInt(yearFilter);
      const res = await adminBillingAPI.listInvoices(params);
      setInvoices(res.data.invoices || []);
    } catch { setInvoices([]); }
    finally { setLoading(false); }
  }, [orgFilter, yearFilter]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const filterStyle = { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 10px', color: C.cream, fontSize: 13 };

  return (
    <CoachLayout>
      <div style={{ padding: 24, background: C.bg, minHeight: '100vh', color: C.cream }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Billing & Invoices</h1>
            <p style={{ margin: '4px 0 0', color: C.muted, fontSize: 13 }}>{invoices.length} invoices</p>
          </div>
          <button onClick={() => setShowGenerate(true)}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: C.accent, color: C.cream, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            + Generate Invoice
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input placeholder="Filter by billing org ID…" value={orgFilter}
            onChange={e => setOrgFilter(e.target.value)} style={{ ...filterStyle, flex: '1 1 160px' }} />
          <input placeholder="Year (e.g. 2026)" value={yearFilter}
            onChange={e => setYearFilter(e.target.value)} style={{ ...filterStyle, width: 140 }} />
          <button onClick={fetchInvoices} style={{ ...filterStyle, cursor: 'pointer', background: C.surface }}>Refresh</button>
        </div>

        {loading ? (
          <div style={{ color: C.muted, textAlign: 'center', padding: 40 }}>Loading…</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}`, color: C.muted }}>
                {['Period', 'Billing Org', 'Total', 'Status', ''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: C.muted, padding: 32 }}>No invoices yet. Click "+ Generate Invoice" to create one.</td></tr>
              )}
              {invoices.map(inv => (
                <tr key={inv.invoice_id} style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }} onClick={() => setSelected(inv)}>
                  <td style={{ padding: '10px 10px', color: C.cream, fontWeight: 600 }}>{inv.period_label}</td>
                  <td style={{ padding: '10px 10px', color: C.muted }}>{inv.billing_org_id}</td>
                  <td style={{ padding: '10px 10px', color: C.peach, fontWeight: 700 }}>{inv.total_dollars || '$0.00'}</td>
                  <td style={{ padding: '10px 10px' }}><StatusBadge status={inv.status} /></td>
                  <td style={{ padding: '10px 10px' }}>
                    <button onClick={e => { e.stopPropagation(); setSelected(inv); }}
                      style={{ background: C.border, border: 'none', borderRadius: 4, color: C.cream, padding: '4px 10px', cursor: 'pointer', fontSize: 11 }}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <InvoiceDetail
          invoice={selected}
          onClose={() => setSelected(null)}
          onRefresh={() => { fetchInvoices(); setSelected(null); }}
        />
      )}

      {showGenerate && (
        <GenerateModal
          onClose={() => setShowGenerate(false)}
          onGenerated={() => { setShowGenerate(false); fetchInvoices(); }}
        />
      )}
    </CoachLayout>
  );
}
